export interface AutoRetryOptions<T> {
  call: () => Promise<T> | void;
  shouldRetry: (_: T | void) => boolean;
  // The initial delay before the first retry, in milliseconds (defaults to 1000 milliseconds).
  initialTimer?: number;
  // The maximum delay between retries, in milliseconds (defaults to 60000 milliseconds, or 1 minute).
  maxTimer?: number;
  onComplete?: (_: any) => void;
  numberOfTries?: number;
  backoffFactor?: number;
  onFailure?: () => void;
  shouldNotRetryOnFailure?: boolean;
}

export const autoRetry = async <T>({
  call,
  initialTimer = 1000,
  maxTimer = 60000,
  shouldRetry,
  onComplete,
  numberOfTries = 5,
  backoffFactor = 2,
  onFailure,
  shouldNotRetryOnFailure = false,
}: AutoRetryOptions<T>) => {
  let tryCount = 0;
  let failCount = 0;
  let currentTimer = initialTimer;

  while (tryCount < numberOfTries) {
    try {
      const result: void | Awaited<T> = await call();
      if (shouldRetry && !shouldRetry(result)) {
        onComplete && onComplete(result);
        break;
      }
      currentTimer = Math.min(maxTimer, currentTimer);
      await new Promise((resolve) => setTimeout(resolve, currentTimer));
      tryCount++;
      failCount = 0;
    } catch (error) {
      if (shouldNotRetryOnFailure) {
        onFailure?.();
        break;
      }
      failCount++;
      const backoffTime = Math.min(maxTimer, Math.round(currentTimer * Math.pow(backoffFactor, failCount)));
      await new Promise((resolve) => setTimeout(resolve, backoffTime));
      tryCount++;
    }
  }
  if (tryCount >= numberOfTries) {
    onFailure?.();
  }
  // maximum retries done or callback finished
};
